import { useState, useEffect } from 'react';
import {
	isValidId,
	updateActiveTask,
	Util,
} from '../../util/Util';
import RefreshButton from '../../components/RefreshButton';
import { useProduction, ProductionLine } from './ProductionContext';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { MIN_DATE } from '../../util/constants';
import { QTY_LIMITS, parseQtyInput, qtyMin, qtyStepAttr, roundQty } from '../../util/quantity';
import { useAuth } from '../../hooks/useAuth';
import { Autocomplete, Chip, Grid, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { v4 as uuidv4 } from 'uuid';
import { businessTodayDate, parseISODay } from "../../util/businessClock";

interface Props {
	setValidateCheck: (value: boolean) => void;
}

const statusColor = (status?: string) => {
	switch (status) {
		case 'FINALIZED':
			return 'success';
		case 'RUNNING':
			return 'warning';
		case 'DRAFT':
			return 'info';
		default:
			return 'default';
	}
};

const ProductionDetail = ({ setValidateCheck }: Props) => {
	const [depots, setDepots] = useState<any[]>([]);
	const [clients, setClients] = useState<any[]>([]);
	const [boms, setBoms] = useState<any[]>([]);
	const [isClientLoading, setIsClientLoading] = useState(false);
	const [isDepotLoading, setIsDepotLoading] = useState(false);
	const [isBomLoading, setIsBomLoading] = useState(false);

	const { setPendingTasks, activeTask, isLocked } = useProduction();
	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const { api } = useAuth();

	const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;
	const headerLocked =
		isLocked || activeTask?.status === 'RUNNING' || activeTask?.status === 'FINALIZED';

	const fetchClients = async () => {
		setIsClientLoading(true);
		const res = await api.get('/clients', {
			params: { Active: true, Type: 'WORKER' },
		});
		if (res?.status === 200) {
			res.data.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
			setClients(res.data);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchClients);
		}
		setIsClientLoading(false);
	};

	const fetchDepots = async () => {
		setIsDepotLoading(true);
		const res = await api.get('/depot/meterred', { params: { Operation: 'Production' } });
		if (res?.status === 200) {
			setDepots(res.data);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchDepots);
		}
		setIsDepotLoading(false);
	};

	const fetchBoms = async () => {
		setIsBomLoading(true);
		const res = await api.get('/prod-boms', {
			params: { Active: true },
		});
		if (res?.status === 200) {
			const raw = res.data;
			const list = Array.isArray(raw) ? raw : raw?.items || raw?.data || [];
			setBoms(
				list.filter((r: any) => r.is_active === undefined || !!r.is_active)
			);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchBoms);
		}
		setIsBomLoading(false);
	};

	const applyBom = async (bomId: number | null) => {
		if (headerLocked) return;
		if (!bomId) {
			updateActiveTask(setPendingTasks, {
				bom_id: null,
				bom_name: '',
				components: [],
			});
			return;
		}

		const res = await api.get(`/prod-boms/${bomId}`);
		if (res?.status === 200) {
			const bom = res.data;
			const scale = Math.max(1, activeTask?.scale || 1);
			const components: ProductionLine[] = (bom.components || []).map(
				(c: any) => {
					const fractional = c.is_fractional === true || c.IsFractional === true;
					const baseQty = Math.max(qtyMin(fractional), Number(c.quantity) || qtyMin(fractional));
					const planned = roundQty(baseQty * scale);
					return {
						key: uuidv4(),
						item: Number(c.item),
						item_desc: String(c.item_desc || '').trim(),
						role: c.role === 'OUTPUT' ? 'OUTPUT' : 'INPUT',
						planned_qty: planned,
						quantity: planned,
						unit: String(c.unit || '').trim(),
						amount: Math.max(0, Number(c.amount ?? 0) || 0),
						isFractional: fractional,
					};
				}
			);
			const bomDepot = bom.depot;
			updateActiveTask(setPendingTasks, {
				bom_id: bom.id,
				bom_name: bom.name,
				// Prefill production depot from BOM depot when not already set
				...(activeTask && (activeTask.depot || -1) < 1 && bomDepot
					? { depot: bomDepot }
					: {}),
				scale,
				components,
			});
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(() => applyBom(bomId));
		}
	};

	const onScaleChanged = (scale: number) => {
		if (headerLocked) return;
		const safeScale = Math.max(qtyMin(true), Math.min(QTY_LIMITS.max, scale || qtyMin(true)));
		const prevScale = activeTask?.scale || 1;
		const components = (activeTask?.components || []).map((c) => {
			const fractional = c.isFractional === true;
			const base =
				prevScale > 0
					? roundQty(c.planned_qty / prevScale)
					: c.planned_qty;
			const planned = roundQty(Math.max(qtyMin(fractional), base * safeScale));
			return {
				...c,
				planned_qty: planned,
				quantity: planned,
			};
		});
		updateActiveTask(setPendingTasks, { scale: safeScale, components });
	};

	const onDepotChanged = (depotId: number) => {
		if (headerLocked) return;
		// Changing production depot clears lines (items are depot-scoped)
		updateActiveTask(setPendingTasks, {
			depot: depotId,
			components: [],
			bom_id: null,
			bom_name: '',
		});
	};

	useEffect(() => {
		fetchClients();
		fetchDepots();
		fetchBoms();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const ok =
			isValidId(clients, activeTask?.receiver) &&
			isValidId(depots, activeTask?.depot) &&
			!!activeTask?.date &&
			!!activeTask?.reference?.trim();
		setValidateCheck(ok);
	}, [activeTask, clients, depots, setValidateCheck]);

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Grid container spacing={2}>
				{activeTask?.status && activeTask.status !== 'local' && (
					<Grid size={{ xs: 12 }}>
						<Chip
							size="small"
							color={statusColor(activeTask.status) as any}
							label={t(`common:production.status.${String(activeTask.status).toLowerCase()}`)}
						/>
					</Grid>
				)}
				<Grid size={{ xs: 12, sm: 6, md: 3 }}>
					<DatePicker
						label={t('common:tables.date')}
						value={activeTask?.date ? parseISODay(activeTask.date) as Date : businessTodayDate()}
						minDate={(parseISODay(minDate) as Date)}
						disabled={headerLocked}
						onChange={(d) => {
							const dateVal =
								d instanceof Date
									? d
									: d
										? new Date(d as unknown as string)
										: new Date();
							updateActiveTask(setPendingTasks, {
								date: Util.formatISODate(dateVal),
							});
						}}
						slotProps={{ textField: { fullWidth: true, size: 'small' } }}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 3 }}>
					<TextField
						fullWidth
						size="small"
						label={t('common:tables.reference')}
						value={activeTask?.reference || ''}
						disabled={headerLocked}
						onChange={(e) =>
							updateActiveTask(setPendingTasks, { reference: e.target.value })
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 3 }}>
					<Autocomplete
						size="small"
						options={clients}
						loading={isClientLoading}
						disabled={headerLocked}
						getOptionLabel={(o) => o.acc_name || ''}
						value={clients.find((c) => c.id === activeTask?.receiver) || null}
						onChange={(_, v) =>
							updateActiveTask(setPendingTasks, { receiver: v?.id ?? -1 })
						}
						renderInput={(params) => (
							<TextField {...params} label={t('common:tables.observer')} />
						)}
					/>
					<RefreshButton onClick={fetchClients} isWorking={isClientLoading} />
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 3 }}>
					<Autocomplete
						size="small"
						options={depots}
						loading={isDepotLoading}
						disabled={headerLocked}
						getOptionLabel={(o) => o.depot_name || o.DepotName || o.name || ''}
						value={depots.find((d) => d.id === activeTask?.depot) || null}
						onChange={(_, v) => onDepotChanged(v?.id ?? -1)}
						renderInput={(params) => (
							<TextField
								{...params}
								label={t('common:production.productionDepot')}
								helperText={t('common:production.productionDepotHelp')}
							/>
						)}
					/>
					<RefreshButton onClick={fetchDepots} isWorking={isDepotLoading} />
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<Autocomplete
						size="small"
						options={boms}
						loading={isBomLoading}
						disabled={headerLocked}
						getOptionLabel={(o) =>
							o.code ? `${o.code} — ${o.name}` : o.name || ''
						}
						value={boms.find((r) => r.id === activeTask?.bom_id) || null}
						onChange={(_, v) => applyBom(v ? v.id : null)}
						renderInput={(params) => (
							<TextField
								{...params}
								label={t('common:production.bom')}
								placeholder={t('common:production.bomOptional')}
							/>
						)}
					/>
					<RefreshButton onClick={fetchBoms} isWorking={isBomLoading} />
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 2 }}>
					<TextField
						fullWidth
						size="small"
						type="number"
						label={t('common:production.scale')}
						value={activeTask?.scale ?? 1}
						disabled={headerLocked}
						inputProps={{ min: qtyMin(true), max: QTY_LIMITS.max, step: qtyStepAttr(true) }}
						onChange={(e) => {
							const parsed = parseQtyInput(e.target.value, true);
							if (parsed === null) return;
							onScaleChanged(parsed);
						}}
						helperText={t('common:production.scaleHelp')}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						size="small"
						label={t('common:report.description')}
						value={activeTask?.description || ''}
						disabled={isLocked}
						onChange={(e) =>
							updateActiveTask(setPendingTasks, { description: e.target.value })
						}
					/>
				</Grid>
			</Grid>
		</LocalizationProvider>
	);
};

export default ProductionDetail;
